import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URI } from "../Common/data";
import Footer from "../Components/Footer";
import Navbar from "../Components/Navbar";
import Aos from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";


function Gallery() {
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});

  const [seo, setSeo] = useState({
    title: "Cine Celebrate - Gallery",
    metaDescription: "",
    metaKeywords: "",
    canonicalUrl: "",
    ogImage: "",
    ogTitle: "",
    ogDescription: "",
  });

  useEffect(() => {
    axios.get(`${API_URI}/page-seo/gallery`).then((res) => {
      if (res.data && res.data.data) setSeo(res.data.data);
    });
  }, []);

  useEffect(() => {
    Aos.init();
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API_URI}/gallery-category/all`);
        setCategories(res.data.data);
        if (res.data.data.length > 0) {
          setSelectedCategoryId(res.data.data[0]._id);
        }
      } catch {
        console.error("Error fetching categories");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchGalleryImages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URI}/gallery`);
        if (response.status === 200) {
          const galleryItems = response.data.data || [];
          setImages(galleryItems);
          galleryItems.forEach((item) => {
            if (!item?.image) {
              return;
            }

            const preloadImage = new Image();
            preloadImage.src = item.image;
          });
        }
      } catch {
        setError("Failed to load gallery images");
      } finally {
        setLoading(false);
      }
    };
    fetchGalleryImages();
  }, []);

  useEffect(() => {
    if (selectedCategoryId) {
      const filtered = images.filter(
        (img) => img.category?._id === selectedCategoryId
      );
      setFilteredImages(filtered);
    }
  }, [selectedCategoryId, images]);

  const openLightbox = (index) => {
    setPhotoIndex(index);
    setIsOpen(true);
  };

  const closeLightbox = () => {
    setIsOpen(false);
  };

  const showNext = () => {
    setPhotoIndex((photoIndex + 1) % filteredImages.length);
  };

  const showPrev = () => {
    setPhotoIndex(
      (photoIndex + filteredImages.length - 1) % filteredImages.length
    );
  };

  const selectedCategory = categories.find(
    (category) => category._id === selectedCategoryId
  );
  const featuredImage = filteredImages[0];

  const markImageLoaded = (key) => {
    setLoadedImages((prev) => ({ ...prev, [key]: true }));
  };

  return (
    <>
      <Helmet>
        <title>
          {seo.metaTitle && seo.metaTitle.toLowerCase() !== "test title"
            ? seo.metaTitle
            : "Cine Celebrate - Gallery"}
        </title>
        <meta
          name="title"
          content={
            seo.metaTitle && seo.metaTitle.toLowerCase() !== "test title"
              ? seo.metaTitle
              : "Cine Celebrate - Gallery"
          }
        />
        <meta
          name="description"
          content={
            seo.metaDescription &&
            seo.metaDescription.toLowerCase() !== "test description"
              ? seo.metaDescription
              : "Cine Celebrate - Gallery"
          }
        />
        <meta
          name="keywords"
          content={
            seo?.metaKeywords && Array.isArray(seo?.metaKeywords)
              ? seo?.metaKeywords.join(", ")
              : "Private Theater, Celebration, Cine Celebrate"
          }
        />
        <link rel="canonical" href={seo?.canonicalUrl} />
        <meta property="og:title" content={seo?.ogTitle} />
        <meta property="og:description" content={seo?.ogDescription} />
        <meta property="og:image" content={seo?.ogImage} />
      </Helmet>
      <Navbar />
      <div className="news-banner news-banner-policy">
        <p>Gallery</p>
        <br />
        <h1>Gallery for Cine Celebrate</h1>
      </div>
      <section className="gallery-page">
        <div className="gallery-page__orb gallery-page__orb--one"></div>
        <div className="gallery-page__orb gallery-page__orb--two"></div>

        <div className="gallery-hero">
          <div className="gallery-hero__copy">
            <p className="gallery-hero__eyebrow">Captured Moments</p>
            <h1>Celebration frames, private screenings, and curated setups.</h1>
            <p className="gallery-hero__text">
              Browse our gallery by vibe, celebration type, or setup style. Each
              category gives a quick feel for how Cine Celebrate transforms a
              private screening into a memorable event.
            </p>

            <div className="gallery-category-strip gallery-category-strip--hero">
              {categories.map((cat) => (
                <button
                  key={cat._id}
                  type="button"
                  className={`gallery-category-strip__button ${
                    selectedCategoryId === cat._id ? "is-active" : ""
                  }`}
                  onClick={() => setSelectedCategoryId(cat._id)}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="gallery-hero__feature">
            <div
              className={`gallery-hero__feature-card ${
                !featuredImage ? "gallery-hero__feature-card--empty" : ""
              }`}
            >
              {featuredImage ? (
                <>
                  <img
                    src={featuredImage.image}
                    alt={featuredImage.category?.name || "Featured gallery frame"}
                    className={loadedImages[featuredImage.image] ? "is-loaded" : ""}
                    onLoad={() => markImageLoaded(featuredImage.image)}
                  />
                  <div className="gallery-hero__feature-overlay">
                    <span>Featured Category</span>
                    <strong>{selectedCategory?.name || "Gallery"}</strong>
                  </div>
                </>
              ) : (
                <span>{loading ? "Loading gallery..." : "No images available yet."}</span>
              )}
            </div>
          </div>
        </div>

        <div className="gallery-shell">
          <div className="gallery-shell__topbar">
            <div>
              <p className="gallery-shell__eyebrow">Browse Collection</p>
              <h2>{selectedCategory?.name || "Gallery Collection"}</h2>
            </div>
            <div className="gallery-shell__counter">
              {filteredImages.length} image{filteredImages.length === 1 ? "" : "s"}
            </div>
          </div>

          {error ? (
            <div className="gallery-grid gallery-grid--empty">{error}</div>
          ) : filteredImages.length ? (
            <div className="gallery-grid">
              {filteredImages.map((img, index) => {
                const cardClassName =
                  index % 5 === 0 ? "gallery-grid__card gallery-grid__card--wide" : "gallery-grid__card";

                return (
                  <button
                    key={img._id || img.image || index}
                    type="button"
                    className={cardClassName}
                    onClick={() => openLightbox(index)}
                    data-aos="fade-up"
                    data-aos-duration="700"
                  >
                    <img
                      src={img.image}
                      alt={img.category?.name || "Gallery"}
                      className={loadedImages[img.image] ? "is-loaded" : ""}
                      onLoad={() => markImageLoaded(img.image)}
                    />
                    <div className="gallery-grid__overlay">
                      <span>{img.category?.name || "Cine Celebrate"}</span>
                      <strong>Open frame</strong>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="gallery-grid gallery-grid--empty">
              {loading ? "Loading gallery..." : "No images in this category."}
            </div>
          )}
        </div>
      </section>

      {/* Custom Lightbox Modal */}
      {isOpen && (
        <div className="gallery-lightbox" onClick={closeLightbox}>
          <button
            type="button"
            className="gallery-lightbox__close"
            onClick={closeLightbox}
            aria-label="Close gallery"
          >
            &times;
          </button>
          {filteredImages.length > 1 && (
            <>
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                onClick={(event) => {
                  event.stopPropagation();
                  showPrev();
                }}
                aria-label="Previous image"
              >
                &#10094;
              </button>
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                onClick={(event) => {
                  event.stopPropagation();
                  showNext();
                }}
                aria-label="Next image"
              >
                &#10095;
              </button>
            </>
          )}
          <div
            className="gallery-lightbox__stage"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="gallery-lightbox__header">
              <p>{selectedCategory?.name || "Gallery"}</p>
              <span>
                {photoIndex + 1} / {filteredImages.length}
              </span>
            </div>
            <img
              src={filteredImages[photoIndex].image}
              alt="Full view"
              className="gallery-lightbox__image"
            />
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default Gallery;
